import { AfterViewInit, Directive, ElementRef, HostBinding, OnDestroy, Renderer2, inject } from '@angular/core';

@Directive({
  selector: '[flowStickyHeader]',
})
export class StickyHeaderDirective implements AfterViewInit, OnDestroy {
  @HostBinding('class.fl-sticky-header') readonly stickyClass = true;
  @HostBinding('class.is-stuck') isStuck = false;

  private readonly el: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  private observer?: IntersectionObserver;
  private toggleContainer?: HTMLElement;
  private toggleButton?: HTMLElement;
  private isExpanded = true;

  ngAfterViewInit(): void {
    this.createToggleContainer();
    this.createToggleButton();

    // position: sticky pins the element inside its nearest scrolling ancestor. We observe the
    // host against that same container so `.is-stuck` (the card styling + toggle) only applies
    // once the element is actually pinned at the top.
    const scrollContainer = this.el.nativeElement.closest('.scrollable-parent-div') as HTMLElement | null;

    // position: sticky is confined to its containing block (the nearest block-level ancestor).
    // Feature component hosts default to display:inline, which caps that containing block at the
    // viewport-locked layout wrapper, so the header would unstick and scroll away on long pages.
    // Promote any inline ancestor up to the scroll container to block so the containing block
    // spans the full scrollable content.
    let ancestor = this.el.nativeElement.parentElement;
    while (ancestor && ancestor !== scrollContainer) {
      if (getComputedStyle(ancestor).display === 'inline') {
        this.renderer.setStyle(ancestor, 'display', 'block');
      }
      ancestor = ancestor.parentElement;
    }

    // The scroll container has top padding; without compensating, `top: 0` pins the header at the
    // padding edge and leaves a gap above it where content scrolls through. Offsetting `top` by
    // the padding pins it flush to the container's top, matching the previous fixed behaviour.
    const paddingTop = scrollContainer ? parseInt(getComputedStyle(scrollContainer).paddingTop, 10) || 0 : 0;
    if (paddingTop) {
      this.renderer.setStyle(this.el.nativeElement, 'top', `-${paddingTop}px`);
    }

    this.observer = new IntersectionObserver(entries => this.onIntersect(entries[0]), {
      root: scrollContainer,
      threshold: [1],
      rootMargin: '-1px 0px 0px 0px',
    });
    this.observer.observe(this.el.nativeElement);
  }

  private onIntersect(entry: IntersectionObserverEntry): void {
    const stuck = entry.intersectionRatio < 1;
    if (stuck === this.isStuck) {
      return;
    }
    this.isStuck = stuck;
    if (!stuck) {
      this.expand();
    }
  }

  private createToggleContainer(): void {
    this.toggleContainer = this.renderer.createElement('div');
    this.renderer.insertBefore(this.el.nativeElement, this.toggleContainer, this.el.nativeElement.firstChild);

    const containerClasses = ['fl-sticky-toggle', 'relative', 'w-full', 'h-10', 'bg-gray-50', 'border-b', 'border-neutral-200', 'z-50', 'rounded-t'];
    containerClasses.forEach(className => this.renderer.addClass(this.toggleContainer, className));
  }

  private createToggleButton(): void {
    this.toggleButton = this.renderer.createElement('button');

    const buttonClasses = ['absolute', 'right-1', 'top-1', 'h-[30px]', 'w-[30px]', 'cursor-pointer', 'hover:bg-gray-100', 'rounded'];
    buttonClasses.forEach(className => this.renderer.addClass(this.toggleButton, className));

    this.renderer.setProperty(this.toggleButton, 'innerHTML', '▼');
    this.renderer.listen(this.toggleButton, 'click', () => this.toggleContent());

    this.renderer.appendChild(this.toggleContainer, this.toggleButton);
  }

  private toggleContent(): void {
    this.isExpanded = !this.isExpanded;
    this.renderer.setProperty(this.toggleButton, 'innerHTML', this.isExpanded ? '▼' : '▲');
    this.renderer.setStyle(this.el.nativeElement, 'height', this.isExpanded ? 'auto' : '40px');
  }

  private expand(): void {
    this.isExpanded = true;
    this.renderer.setProperty(this.toggleButton, 'innerHTML', '▼');
    this.renderer.removeStyle(this.el.nativeElement, 'height');
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
